import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

const AboutContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 56px 20px 40px;
  max-width: 780px;
  margin: 0 auto;
`;

const AboutTitle = styled.h1`
  font-size: clamp(2.1rem, 5vw, 2.75rem);
  margin: 0 0 36px;
  font-weight: 800;
  letter-spacing: -0.03em;
  text-align: center;
  background: linear-gradient(135deg, var(--primary-700) 0%, var(--primary-color) 60%, var(--info-color) 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Section = styled.section`
  margin-bottom: 28px;
  width: 100%;
  background: var(--surface-0);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  padding: 28px 30px;
  box-shadow: var(--shadow-sm);
`;

const SectionTitle = styled.h2`
  font-size: 1.4rem;
  margin: 0 0 16px;
  color: var(--ink-900);
  font-weight: 700;
  letter-spacing: -0.02em;
  display: flex;
  align-items: center;
  gap: 12px;

  &::before {
    content: '';
    width: 6px;
    height: 22px;
    border-radius: 3px;
    background: var(--grad-brand-soft);
    flex-shrink: 0;
  }
`;

const SectionContent = styled.div`
  color: var(--ink-600);
  line-height: 1.75;
  font-size: 1.02rem;

  p {
    margin-bottom: 14px;
  }

  p:last-child {
    margin-bottom: 0;
  }

  ul, ol {
    margin-left: 20px;
    margin-bottom: 14px;
  }

  li {
    margin-bottom: 8px;
  }
`;

const Button = styled(Link)`
  background-color: var(--secondary-color);
  color: white;
  padding: 14px 30px;
  border-radius: var(--radius-pill);
  text-decoration: none;
  font-weight: 600;
  font-size: 1rem;
  box-shadow: var(--shadow-accent-green);
  transition: background-color var(--dur-base) var(--ease-out),
    transform var(--dur-base) var(--ease-spring),
    box-shadow var(--dur-base) var(--ease-out);
  align-self: center;
  margin-top: 16px;

  &:hover {
    background-color: var(--secondary-700);
    transform: translateY(-3px);
    box-shadow: 0 12px 28px rgba(76, 175, 80, 0.36);
  }

  &:active {
    transform: translateY(0) scale(0.98);
  }
`;

const AuthorSection = styled.div`
  background: linear-gradient(135deg, rgba(33, 150, 243, 0.06), rgba(33, 150, 243, 0.02));
  border: 1px solid var(--border);
  border-left: 4px solid var(--primary-color);
  padding: 24px 26px;
  margin-bottom: 28px;
  border-radius: var(--radius-md);
  color: var(--ink-600);
  line-height: 1.75;
  font-size: 1.02rem;

  p {
    margin: 0;
  }
`;

const AboutPage: React.FC = () => {
  return (
    <AboutContainer>
      <AboutTitle>About ThreadViz</AboutTitle>
      
      <AuthorSection>
        <p>
          ThreadViz is a side project developed by a passionate computer science college student
          with a deep interest in operating systems and concurrent programming. This project
          represents a step in my journey to build an operating system from the ground up,
          helping me visualize and understand critical concurrency concepts that form the
          foundation of modern OS design.
        </p>
      </AuthorSection>
      
      <Section>
        <SectionTitle>Project Overview</SectionTitle>
        <SectionContent>
          <p>
            ThreadViz is an interactive educational tool designed to help developers, students, and educators
            understand complex concurrency concepts through visual simulation. By providing real-time
            visualizations of classic concurrency problems, ThreadViz makes abstract multithreading concepts
            more tangible and easier to comprehend.
          </p>
          <p>
            Whether you're a student learning about operating systems, a developer trying to understand
            thread synchronization, or an educator teaching concurrent programming, ThreadViz offers an
            intuitive interface to explore the dynamics of parallel execution.
          </p>
        </SectionContent>
      </Section>
      
      <Section>
        <SectionTitle>Learning Objectives</SectionTitle>
        <SectionContent>
          <p>ThreadViz helps users understand:</p>
          <ul>
            <li>Thread states and lifecycles in concurrent applications</li>
            <li>Resource allocation and sharing between threads</li>
            <li>Deadlock causes, detection, and prevention strategies</li>
            <li>Synchronization mechanisms (mutexes, semaphores, barriers, etc.)</li>
            <li>Patterns for efficient concurrent programming</li>
            <li>Race conditions and how to avoid them</li>
          </ul>
        </SectionContent>
      </Section>
      
      <Section>
        <SectionTitle>Technology Stack</SectionTitle>
        <SectionContent>
          <p>ThreadViz is built using modern web technologies:</p>
          <ul>
            <li>React for the user interface</li>
            <li>TypeScript for type-safe JavaScript code</li>
            <li>Styled Components for component styling</li>
            <li>Canvas API for thread visualization</li>
          </ul>
        </SectionContent>
      </Section>
      
      <Section>
        <SectionTitle>How to Use</SectionTitle>
        <SectionContent>
          <p>Using ThreadViz is simple:</p>
          <ol>
            <li>Select a simulation type from the available options</li>
            <li>Configure parameters like thread count, resource count, and speed</li>
            <li>Start the simulation and observe thread behavior</li>
            <li>Use controls to pause, resume, or reset as needed</li>
            <li>Read the event log to track detailed thread state changes</li>
          </ol>
          <p>
            Experiment with different configurations to see how they affect thread behavior and
            potential race conditions or deadlocks.
          </p>
        </SectionContent>
      </Section>
      
      <Section>
        <SectionTitle>The Bigger Picture: OS Development</SectionTitle>
        <SectionContent>
          <p>
            This visualization tool is part of my broader educational journey toward building an 
            operating system from scratch as a summer project. By implementing and visualizing 
            these concurrency patterns, I'm developing a deeper understanding of:
          </p>
          <ul>
            <li>Thread scheduling and management</li>
            <li>Resource allocation strategies</li>
            <li>Inter-process communication techniques</li>
            <li>Memory management and protection</li>
            <li>Synchronization primitives implementation</li>
          </ul>
          <p>
            Stay tuned for more details about my upcoming OS development project. I'll be sharing 
            the architecture, design decisions, implementation challenges, and educational resources 
            that emerge from this journey.
          </p>
        </SectionContent>
      </Section>
      
      <Button to="/simulation">Try It Now</Button>
    </AboutContainer>
  );
};

export default AboutPage; 